/**
 * ZCard — Zephyr OS Design System
 *
 * Standard card with left-border accent, hover states, inline expansion,
 * and action confirmation (green flash / red error).
 * Spec: design-system.md Section 7 (Cards) and 7a (Hover States).
 */

import { useState, useEffect, useRef } from 'react';

/**
 * useConfirmation hook — exposes confirmation state for ZCard.
 *
 * Usage:
 *   const { state, confirm, fail, reset } = useConfirmation();
 *   <ZCard confirmationState={state} ... />
 *   // After save: confirm()
 *   // After error: fail()
 */
export function useConfirmation(duration = 5000) {
  const [state, setState] = useState(null); // null | 'success' | 'error'
  const timerRef = useRef(null);

  const confirm = () => {
    setState('success');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setState(null), duration);
  };

  const fail = () => {
    setState('error');
    // Error persists until manual reset or next interaction
  };

  const reset = () => {
    clearTimeout(timerRef.current);
    setState(null);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { state, confirm, fail, reset };
}

export default function ZCard({
  accent = '#6B35B8',
  expanded = false,
  onToggle,
  tinted = false,
  confirmationState = null, // null | 'success' | 'error'
  className = '',
  children,
}) {
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef(null);

  // Auto-scroll expanded card into view
  useEffect(() => {
    if (expanded && cardRef.current) {
      const timer = setTimeout(() => {
        const rect = cardRef.current.getBoundingClientRect();
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [expanded]);

  // Determine left border color based on confirmation state
  let borderLeftColor = accent;
  let borderLeftOpacity = hovered ? 0.80 : 0.50;
  if (confirmationState === 'success') {
    borderLeftColor = '#10B981';
    borderLeftOpacity = 0.70;
  } else if (confirmationState === 'error') {
    borderLeftColor = '#E24B4A';
    borderLeftOpacity = 0.70;
  }

  // Background
  let bg = tinted ? `${accent}0F` : 'rgba(255,255,255,0.03)'; // 0F = ~6% opacity
  if (hovered && !tinted) bg = 'rgba(255,255,255,0.05)';
  if (hovered && tinted) bg = `${accent}1A`; // 1A = ~10% opacity
  if (confirmationState === 'success') bg = 'rgba(16,185,129,0.04)';

  // Borders
  const borderOther = tinted
    ? `1px solid ${accent}${hovered ? '2E' : '1F'}` // 1F=~12%, 2E=~18%
    : `1px solid rgba(255,255,255,${hovered ? '0.10' : '0.06'})`;

  return (
    <div
      ref={cardRef}
      onClick={onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`scroll-mt-24 ${className}`}
      style={{
        background: bg,
        borderLeft: `3px solid ${borderLeftColor}`,
        borderLeftColor: borderLeftColor,
        borderTop: borderOther,
        borderRight: borderOther,
        borderBottom: borderOther,
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
        opacity: borderLeftOpacity >= 0.50 ? 1 : undefined, // keep full opacity
        borderRadius: 10,
        padding: '14px 16px',
        cursor: onToggle ? 'pointer' : 'default',
        transition: 'all 150ms ease',
        position: 'relative',
      }}
    >
      {children}

      {/* Confirmation label */}
      {confirmationState === 'success' && (
        <div style={{
          fontSize: 10,
          fontFamily: "'Inter', sans-serif",
          fontWeight: 400,
          color: '#10B981',
          marginTop: 8,
        }}>
          ✓ Saved
        </div>
      )}
      {confirmationState === 'error' && (
        <div style={{
          fontSize: 10,
          fontFamily: "'Inter', sans-serif",
          fontWeight: 400,
          color: '#E24B4A',
          marginTop: 8,
        }}>
          ✗ Failed to save
        </div>
      )}
    </div>
  );
}
